import { SEO } from '../../components/seo/SEO';
import { Link } from 'react-router-dom';
import { categories } from '../../data/categories';
import { getBaseUrl } from '../../lib/utils';

const categoryMeta: Record<string, { tagline: string; count: number }> = {
  'ai-creation':  { tagline: 'AIで作る過程を、そのまま残す', count: 8 },
  'build-log':    { tagline: '判断・迷い・失敗も含めた制作記録', count: 5 },
  'productivity': { tagline: '作業を速く・正確にするための整理', count: 6 },
  'tools-in-use': { tagline: '使った結果だけを書く', count: 4 },
};

const readingGuides = [
  {
    icon: 'ri-hammer-line',
    title: '何かを作りたい・作っている',
    desc: 'AIで実装・UI再現・仕様書化など、制作プロジェクトが進行中の方はここから。',
    slugs: ['ai-creation', 'build-log'],
  },
  {
    icon: 'ri-file-text-line',
    title: 'プロセスを整理・仕様化したい',
    desc: 'プロンプト設計・ワークフロー・テンプレート化など、再現性を高めたい方向け。',
    slugs: ['productivity'],
  },
  {
    icon: 'ri-tools-line',
    title: '使えるツールを探している',
    desc: '実際に使って役立ったツールの使い方と所感。紹介ではなく使った結果を書く。',
    slugs: ['tools-in-use'],
  },
];

export default function CategoriesPage() {
  const baseUrl = getBaseUrl();
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'カテゴリ一覧 — つくってなんぼ',
    description: 'AIツール活用・制作フロー・UI再現・比較検証などのカテゴリ一覧。',
    url: `${baseUrl}/categories`,
  };

  return (
    <>
      <SEO
        title="カテゴリ一覧 | つくってなんぼ — AI実践メディア"
        description="AIツール活用、制作フロー、UI再現、比較検証などのカテゴリ一覧。実際に作って詰まって直した過程を再現できる形で整理した実践メディア。"
        canonical="/categories"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#FDFAF6]">

        {/* Page header */}
        <div className="pt-24 sm:pt-32 pb-8 border-b border-[#E8E0D5]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#5B7B6F] mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              Categories
            </p>
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#2C2416] mb-3 leading-snug"
              style={{ fontFamily: "'Noto Serif JP', 'Georgia', serif", letterSpacing: '-0.01em' }}
            >
              カテゴリ一覧
            </h1>
            <p className="text-sm text-[#7A6E62] leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              「何の話題を扱うか」ではなく、「今何を作っていて、その過程で何が役立つか」から記事を作っています。
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">

          {/* Category list */}
          <div className="space-y-3 mb-12">
            {categories.map((cat) => {
              const meta = categoryMeta[cat.slug];
              return (
                <Link
                  key={cat.slug}
                  to={`/categories/${cat.slug}`}
                  className="flex items-start gap-4 p-4 sm:p-5 border border-[#E8E0D5] rounded-lg hover:border-[#C8BFB5] hover:bg-[#F8F3EC] transition-all cursor-pointer group bg-[#FDFAF6]"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#F0EBE3] rounded-md group-hover:bg-[#E8E0D5] transition-colors">
                    <i className={`${cat.icon} text-base text-[#5B7B6F]`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h2
                        className="text-sm font-semibold text-[#2C2416] group-hover:text-[#5B7B6F] transition-colors"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {cat.name}
                      </h2>
                      {meta && (
                        <span className="text-xs text-[#9A8E84] flex-shrink-0" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                          {meta.count}本
                        </span>
                      )}
                    </div>
                    {meta && (
                      <p className="text-xs text-[#5B7B6F] font-medium mb-1" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        {meta.tagline}
                      </p>
                    )}
                    <p className="text-xs text-[#7A6E62] leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {cat.description}
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-[#C8BFB5] group-hover:text-[#5B7B6F] transition-colors flex-shrink-0 mt-1"></i>
                </Link>
              );
            })}
          </div>

          {/* Reading guide */}
          <div className="border-t border-[#EDE7DF] pt-10 mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              どこから読む？
            </p>
            <div className="space-y-4">
              {readingGuides.map((guide) => (
                <div key={guide.title} className="flex items-start gap-4 py-4 border-b border-[#EDE7DF] last:border-0">
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#F0EBE3] rounded-md">
                    <i className={`${guide.icon} text-sm text-[#5B7B6F]`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-[#2C2416] mb-1" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {guide.title}
                    </h3>
                    <p className="text-xs text-[#7A6E62] leading-relaxed mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {guide.desc}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {guide.slugs.map((s) => {
                        const cat = categories.find((c) => c.slug === s);
                        return cat ? (
                          <Link
                            key={s}
                            to={`/categories/${s}`}
                            className="inline-flex items-center gap-1 text-xs text-[#5B7B6F] border border-[#5B7B6F]/30 px-2.5 py-1 rounded-full hover:bg-[#5B7B6F] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                          >
                            {cat.name}
                          </Link>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom nav */}
          <div className="flex flex-wrap gap-4 pt-2">
            <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              <i className="ri-article-line text-[#5B7B6F]"></i>
              記事一覧
            </Link>
            <span className="text-[#D4C9BE]">·</span>
            <Link to="/about" className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              About
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
